import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useRef, useState } from 'react';
import {
  Badge,
  buttonClasses,
  Kanban,
  KanbanCardBody,
  Modal,
  Page,
  PageHeader,
  Toolbar,
  type KanbanColor,
  type KanbanMove,
  type KanbanPage,
  type KanbanStage,
} from '@evogard/ui';
import {
  TICKET_STATUSES,
  TICKET_TRANSITIONS,
  ticketPriorityLabel,
  ticketStatusLabel,
  validTransitions,
  type TicketStatus,
} from '@evogard/domain';
import type { TicketSummary } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { Deadline, refusalFor, useIsConsultant, viewerFrom } from './common.js';
import {
  TicketFilterButton,
  TicketMineToggle,
  ticketSearch,
  useTicketFilters,
} from './filters.js';
import { StatusForm } from './StatusForm.js';

/**
 * O quadro das demandas — as SITUAÇÕES são as colunas (23/09/2026).
 *
 * É a mesma mesa de `TicketsScreen.tsx` vista de outro jeito: o mesmo recorte
 * no endereço (`useTicketFilters` — busca, filtros, "Todas/Comigo"), a mesma
 * rota de listagem (`GET /tickets` com `situacao=` e página, uma por coluna)
 * e a mesma regra para mover. "Ver lista" e "Ver quadro" trocam de visão sem
 * perder o recorte.
 *
 * O componente é o `Kanban` genérico de `@evogard/ui`. Ele foi desenhado para
 * fases que a pessoa cria; aqui as colunas são FIXAS, as cinco situações de
 * `TICKET_STATUSES` — por isso nenhuma `stageActions`: não se cria, renomeia,
 * reordena nem arquiva situação.
 *
 * ## Mover um cartão é mudar a situação — e só isso
 *
 * O mesmo `PATCH /tickets/:id/status` do "Mudar situação", sob as mesmas três
 * conferências, feitas ANTES da chamada para a recusa vir com o motivo:
 *
 * 1. a transição existe (`TICKET_TRANSITIONS` — concluída e cancelada não
 *    voltam);
 * 2. a pessoa tem `tickets.tickets:concluir`;
 * 3. o papel deixa (`refusalFor('close')` — o responsável, o líder do setor ou
 *    quem vê qualquer demanda).
 *
 * Soltar em **Concluída** ou **Cancelada** abre a confirmação (o
 * `StatusForm`, com a situação já escolhida): as duas encerram a demanda sem
 * volta, e cancelar exige o motivo. "Voltar" devolve o cartão.
 *
 * **Não há ordem dentro da coluna.** A demanda não tem posição gravada — a
 * coluna segue a ordem da mesa —, então arrastar para cima ou para baixo na
 * mesma coluna volta, dizendo isso. Fingir que a ordem ficou seria a tela
 * afirmando o que o servidor não guardou.
 *
 * Quem não pode mudar situação (sem `concluir`, ou o consultor) vê o quadro e
 * não arrasta nada — `onMoveCard` ausente.
 */

/** A cor de cada situação — a cor pinta só o cabeçalho da coluna. */
const STATUS_COLORS: Record<TicketStatus, KanbanColor> = {
  // chaves = `TicketStatus`, o valor gravado — valor, não identificador
  // @valor
  aberta: 'azul',
  em_andamento: 'laranja',
  aguardando_solicitante: 'amarelo',
  concluida: 'verde',
  cancelada: 'cinza',
};

/** Encerram a demanda sem volta: pedem confirmação, e cancelar pede o motivo. */
const FINAL: readonly TicketStatus[] = ['concluida', 'cancelada'];

const PER_PAGE = 20;

interface Confirming {
  ticket: TicketSummary;
  to: TicketStatus;
  resolve: () => void;
  reject: (reason: Error) => void;
}

export function TicketBoardScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const filters = useTicketFilters();
  const navigate = useNavigate();
  const client = useQueryClient();
  const isConsultant = useIsConsultant();
  const viewer = viewerFrom(context, isConsultant);
  const canChangeStatus =
    can(context, 'tickets.tickets', 'concluir') && isConsultant === false;

  /* os cartões que já carregaram — o movimento chega só com o id */
  const loaded = useRef(new Map<string, TicketSummary>());
  const [confirming, setConfirming] = useState<Confirming | null>(null);

  /* o filtro de situação da mesa escolhe quais COLUNAS aparecem */
  const stages: KanbanStage[] = TICKET_STATUSES.filter(
    (s) => filters.state.situacao.length === 0 || filters.state.situacao.includes(s),
  ).map((s) => ({ id: s, name: ticketStatusLabel(s), color: STATUS_COLORS[s] }));

  const loadPage = useCallback(
    async (
      stageId: string,
      cursor: string | null,
    ): Promise<KanbanPage<TicketSummary>> => {
      const page = cursor ? Number(cursor) : 1;
      const r = await api.tickets.list({
        /* a coluna é UMA situação — ela vence o filtro de situação do recorte */
        query: { ...filters.query, situacao: stageId, page, perPage: PER_PAGE },
      });
      const body = ok(r, 200, 'Não foi possível carregar esta coluna.');
      for (const d of body.items) loaded.current.set(String(d.id), d);
      return {
        items: body.items,
        total: body.total,
        next: page * PER_PAGE < body.total ? String(page + 1) : null,
      };
    },
    // o recorte inteiro já está em `filters.key`
    [...filters.key],
  );

  const changeStatus = async (d: TicketSummary, to: TicketStatus) => {
    const r = await api.tickets.changeStatus({
      params: { id: d.id },
      body: { situacao: to },
    });
    ok(r, 200, 'Não foi possível mudar a situação.');
    void client.invalidateQueries({ queryKey: ['demandas'] });
  };

  const moveCard = async (move: KanbanMove) => {
    const d = loaded.current.get(move.cardId);
    const from = move.fromStageId as TicketStatus;
    const to = move.toStageId as TicketStatus;
    if (!d) throw new Error('Recarregue a página e tente de novo.');

    if (from === to) {
      throw new Error(
        'A ordem dentro da coluna segue a mesa; o quadro só muda a situação.',
      );
    }
    if (!validTransitions(from, TICKET_TRANSITIONS).includes(to)) {
      throw new Error(
        FINAL.includes(from)
          ? `${ticketStatusLabel(from)} encerra o ticket: não há volta.`
          : `De ${ticketStatusLabel(from).toLowerCase()} não se vai para ${ticketStatusLabel(to).toLowerCase()}.`,
      );
    }
    const refusal = refusalFor('close', d, viewer, context?.user.id);
    if (refusal) throw new Error(refusal);

    if (FINAL.includes(to)) {
      /* a confirmação resolve ou rejeita o movimento — "Voltar" devolve o cartão */
      await new Promise<void>((resolve, reject) =>
        setConfirming({ ticket: d, to, resolve, reject }),
      );
      loaded.current.set(move.cardId, { ...d, situacao: to });
      return;
    }

    await changeStatus(d, to);
    loaded.current.set(move.cardId, { ...d, situacao: to });
  };

  return (
    <Page measure="cheia">
      <PageHeader
        title="Quadro de tickets"
        actions={
          /* a lista, com o MESMO recorte — o endereço carrega os filtros */
          <Link
            to="/tickets"
            search={(current: Record<string, unknown>) => current}
            className={buttonClasses({ variant: 'contorno' })}
          >
            Ver lista
          </Link>
        }
      >
        Os tickets de {context?.currentTenant.nome} por situação.
        {canChangeStatus
          ? ' Arraste um cartão para outra coluna para mudar a situação.'
          : ' Mudar a situação exige a permissão de concluir tickets.'}
      </PageHeader>

      <Toolbar
        search={ticketSearch(filters)}
        toolbar={
          <>
            {isConsultant === false && <TicketMineToggle filters={filters} />}
            <TicketFilterButton filters={filters} />
          </>
        }
      />

      <Kanban<TicketSummary>
        label="Quadro de tickets"
        stages={stages}
        cardId={(d) => String(d.id)}
        cardLabel={(d) => `${d.code} — ${d.title}`}
        loadPage={loadPage}
        /* recorte novo, ou outra base: as colunas recomeçam da primeira página */
        reloadKey={JSON.stringify([tenantId, ...filters.key])}
        renderCard={(d) => (
          <KanbanCardBody
            tags={
              <>
                <span className="font-mono text-xs text-texto-suave">{d.code}</span>
                {d.priority !== 'normal' && (
                  <Badge tone={d.priority === 'alta' ? 'alerta' : 'neutro'}>
                    {ticketPriorityLabel(d.priority)}
                  </Badge>
                )}
              </>
            }
            title={d.title}
            detail={
              <>
                {d.categoria?.nome ?? 'Sem categoria'} ·{' '}
                <Deadline dueAt={d.dueAt} completed={Boolean(d.completedAt)} />
              </>
            }
            assignee={d.assignee?.nome ?? null}
          />
        )}
        onOpenCard={(d) =>
          void navigate({ to: '/tickets/$id', params: { id: String(d.id) } })
        }
        onMoveCard={canChangeStatus ? moveCard : undefined}
        emptyColumnText={
          filters.active
            ? 'Nenhum ticket nesta situação com esses filtros.'
            : 'Nenhum ticket nesta situação.'
        }
      />

      {confirming && (
        <Modal
          open
          onOpenChange={(open) => {
            if (open) return;
            confirming.reject(new Error('A situação não mudou.'));
            setConfirming(null);
          }}
          title={
            confirming.to === 'cancelada' ? 'Cancelar o ticket' : 'Concluir o ticket'
          }
          descricao={`Ticket ${confirming.ticket.code}`}
        >
          <StatusForm
            ticket={confirming.ticket}
            initial={confirming.to}
            onDone={() => {
              confirming.resolve();
              setConfirming(null);
            }}
            onBack={() => {
              confirming.reject(new Error('A situação não mudou.'));
              setConfirming(null);
            }}
          />
        </Modal>
      )}
    </Page>
  );
}
